package sarka.design.skoria.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import sarka.design.skoria.network.AuthRepository

data class VerifyEmailUiState(
    val username: String = "",
    val email: String = "",
    val code: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val verificationSuccess: Boolean = false,
    val resendMessage: String? = null
)

class VerifyEmailViewModel(
    username: String,
    email: String,
    private val authRepository: AuthRepository = AuthRepository()
) : ViewModel() {

    private val _state = MutableStateFlow(VerifyEmailUiState(username = username, email = email))
    val state: StateFlow<VerifyEmailUiState> = _state

    init {
        // Fetch email if not provided (e.g., coming from login screen)
        if (email.isBlank() && username.isNotBlank()) {
            viewModelScope.launch {
                val result = authRepository.getUserEmail(username)
                if (result.isSuccess) {
                    _state.value = _state.value.copy(email = result.getOrNull() ?: "")
                }
            }
        }
    }

    fun onCodeChange(value: String) {
        // Only allow digits and max 6 characters
        if (value.length <= 6 && value.all { it.isDigit() }) {
            _state.value = _state.value.copy(code = value, error = null)
        }
    }

    fun verifyEmail() {
        val current = _state.value
        
        if (current.code.length != 6) {
            _state.value = current.copy(error = "Please enter the 6-digit code")
            return
        }

        _state.value = current.copy(isLoading = true, error = null)

        viewModelScope.launch {
            val result = authRepository.verifyEmail(current.username, current.code)
            _state.value = if (result.isSuccess) {
                current.copy(isLoading = false, verificationSuccess = true)
            } else {
                current.copy(
                    isLoading = false,
                    error = result.exceptionOrNull()?.message ?: "Verification failed"
                )
            }
        }
    }

    fun resendCode() {
        val current = _state.value
        _state.value = current.copy(isLoading = true, error = null, resendMessage = null)

        viewModelScope.launch {
            val result = authRepository.resendCode(current.username)
            _state.value = if (result.isSuccess) {
                current.copy(
                    isLoading = false,
                    resendMessage = "Verification code sent to ${current.email}",
                    code = "" // Clear the code field
                )
            } else {
                current.copy(
                    isLoading = false,
                    error = result.exceptionOrNull()?.message ?: "Failed to resend code"
                )
            }
        }
    }
}
